import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authStore } from '../api/client';
import { listMyOrgs, updateOrg } from '../api/orgs';
import {
  listAptitudeTests,
  getAptitudeTest,
  createAptitudeTest,
  addAptitudeQuestion,
} from '../api/aptitudeTests';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';

const EMPTY_TEST = {
  name: '',
  passPercentage: 60,
  timeLimitMinutes: 30,
};

const EMPTY_QUESTION = {
  position: 0,
  questionText: '',
  optionA: '',
  optionB: '',
  optionC: '',
  optionD: '',
  correctOption: 'A',
};

export default function AptitudeTestsPage() {
  const toast = useToast();
  const role = authStore.role;
  const canMutate = role === 'SUPER_ADMIN' || role === 'ORG_ADMIN' || role === 'INTERVIEWER';

  const [orgId, setOrgId] = useState(null);
  const [tests, setTests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_TEST);
  const [questionForm, setQuestionForm] = useState(EMPTY_QUESTION);
  const [submitting, setSubmitting] = useState(false);
  const [defaultTestId, setDefaultTestId] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);

  useEffect(() => {
    const email = authStore.user?.email;
    if (!email) return;
    listMyOrgs(email)
      .then((res) => {
        const orgs = Array.isArray(res.data) ? res.data : [];
        if (orgs.length > 0) {
          setOrgId(orgs[0].id);
          setDefaultTestId(orgs[0].aptitudeTestId || '');
        }
      })
      .catch(() => {});
  }, []);

  const reloadList = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await listAptitudeTests({ orgId });
      const list = Array.isArray(res.data) ? res.data : [];
      setTests(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      const res = await getAptitudeTest(id);
      setDetail(res.data);
      setQuestionForm((prev) => ({
        ...prev,
        position: Array.isArray(res.data?.questions) ? res.data.questions.length : 0,
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to load test');
    }
  }, [toast]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!orgId) return;
    setSubmitting(true);
    try {
      const res = await createAptitudeTest({
        orgId,
        name: createForm.name.trim(),
        passPercentage: Number(createForm.passPercentage),
        timeLimitMinutes: Number(createForm.timeLimitMinutes),
      });
      toast.success('Aptitude test created');
      setCreateOpen(false);
      setCreateForm(EMPTY_TEST);
      await reloadList();
      setSelectedId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const payload = {
        position: Number(questionForm.position),
        questionText: questionForm.questionText.trim(),
        optionA: questionForm.optionA.trim(),
        optionB: questionForm.optionB.trim(),
        optionC: questionForm.optionC.trim() || undefined,
        optionD: questionForm.optionD.trim() || undefined,
        correctOption: questionForm.correctOption,
      };
      const res = await addAptitudeQuestion(selectedId, payload);
      setDetail(res.data);
      setQuestionForm({
        ...EMPTY_QUESTION,
        position: (res.data.questions?.length ?? 0),
      });
      toast.success('Question added');
      await reloadList();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Add question failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDefault = async () => {
    if (!orgId) return;
    setSavingDefault(true);
    try {
      const payload = defaultTestId
        ? { aptitudeTestId: defaultTestId }
        : { clearAptitudeTestId: true };
      await updateOrg(orgId, payload);
      toast.success('Default aptitude test updated');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSavingDefault(false);
    }
  };

  return (
    <div className="page-pad page-y space-y-5 max-w-6xl mx-auto">
      <PageHeader
        title="Aptitude Tests"
        subtitle="Pre-interview MCQ tests that gate interview invites until the candidate passes."
        sticky={false}
        action={(
          <Link to="/settings" className="btn btn-sm btn-secondary">
            Back to settings
          </Link>
        )}
      />

      <section className="card p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Org default test</h2>
        <p className="text-xs text-slate-500">
          New applications use this test automatically unless overridden per application.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <select
              className="input w-full"
              value={defaultTestId}
              onChange={(e) => setDefaultTestId(e.target.value)}
            >
              <option value="">No default test</option>
              {tests.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={savingDefault || !orgId}
            onClick={handleSaveDefault}
          >
            {savingDefault ? 'Saving…' : 'Save default'}
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <aside className="lg:col-span-4 card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Tests</h2>
            {canMutate && (
              <button type="button" className="btn btn-xs btn-primary" onClick={() => setCreateOpen(true)}>
                + New
              </button>
            )}
          </div>
          {loading ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : tests.length === 0 ? (
            <p className="text-xs text-slate-400">No tests yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {tests.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition ${
                      selectedId === t.id ? 'bg-teal-50 text-teal-800 font-semibold shadow-xs' : 'hover:bg-canvas text-ink-soft'
                    }`}
                  >
                    <p className="font-medium">{t.name}</p>
                    <p className="text-xs font-mono text-ink-faint">
                      {t.questions?.length ?? 0} questions · {t.timeLimitMinutes} min · pass {t.passPercentage}%
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="lg:col-span-8 card p-5 space-y-5">
          {!selectedId || !detail ? (
            <p className="text-sm text-slate-500">Select a test to view questions and add new ones.</p>
          ) : (
            <>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{detail.name}</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Pass {detail.passPercentage}% · {detail.timeLimitMinutes} minutes · {detail.questions?.length ?? 0} questions
                </p>
              </div>

              {(detail.questions || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic">No questions yet.</p>
              ) : (
                <ol className="space-y-4 list-decimal list-inside">
                  {detail.questions.map((q) => (
                    <li key={q.id} className="border border-slate-200 rounded-lg p-4 text-sm">
                      <p className="font-medium text-slate-800 mb-2">{q.questionText}</p>
                      <ul className="space-y-1 text-slate-600 ml-4">
                        <li>A. {q.optionA}{q.correctOption === 'A' ? ' ✓' : ''}</li>
                        <li>B. {q.optionB}{q.correctOption === 'B' ? ' ✓' : ''}</li>
                        {q.optionC && <li>C. {q.optionC}{q.correctOption === 'C' ? ' ✓' : ''}</li>}
                        {q.optionD && <li>D. {q.optionD}{q.correctOption === 'D' ? ' ✓' : ''}</li>}
                      </ul>
                    </li>
                  ))}
                </ol>
              )}

              {canMutate && (
                <form onSubmit={handleAddQuestion} className="border-t border-slate-200 pt-5 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-800">Add question</h3>
                  <textarea
                    className="input w-full min-h-[80px]"
                    placeholder="Question text"
                    value={questionForm.questionText}
                    onChange={(e) => setQuestionForm((f) => ({ ...f, questionText: e.target.value }))}
                    required
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input className="input" placeholder="Option A" value={questionForm.optionA} onChange={(e) => setQuestionForm((f) => ({ ...f, optionA: e.target.value }))} required />
                    <input className="input" placeholder="Option B" value={questionForm.optionB} onChange={(e) => setQuestionForm((f) => ({ ...f, optionB: e.target.value }))} required />
                    <input className="input" placeholder="Option C (optional)" value={questionForm.optionC} onChange={(e) => setQuestionForm((f) => ({ ...f, optionC: e.target.value }))} />
                    <input className="input" placeholder="Option D (optional)" value={questionForm.optionD} onChange={(e) => setQuestionForm((f) => ({ ...f, optionD: e.target.value }))} />
                  </div>
                  <div className="flex flex-wrap gap-3 items-center">
                    <label className="text-xs text-slate-600">
                      Correct answer
                      <select
                        className="input ml-2"
                        value={questionForm.correctOption}
                        onChange={(e) => setQuestionForm((f) => ({ ...f, correctOption: e.target.value }))}
                      >
                        {['A', 'B', 'C', 'D'].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </label>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                      {submitting ? 'Adding…' : 'Add question'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </main>
      </div>

      <Modal open={createOpen} onClose={() => !submitting && setCreateOpen(false)} title="New aptitude test">
        <form onSubmit={handleCreate} className="space-y-4">
          <input
            className="input w-full"
            placeholder="Test name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600">
              Pass %
              <input
                type="number"
                min={0}
                max={100}
                className="input w-full mt-1"
                value={createForm.passPercentage}
                onChange={(e) => setCreateForm((f) => ({ ...f, passPercentage: e.target.value }))}
                required
              />
            </label>
            <label className="text-xs text-slate-600">
              Time limit (minutes)
              <input
                type="number"
                min={1}
                max={180}
                className="input w-full mt-1"
                value={createForm.timeLimitMinutes}
                onChange={(e) => setCreateForm((f) => ({ ...f, timeLimitMinutes: e.target.value }))}
                required
              />
            </label>
          </div>
          <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create test'}
          </button>
        </form>
      </Modal>
    </div>
  );
}
